import { sha256 } from 'js-sha256';

/**
 * The staff screen mark: who was looking, recoverable from a clipping of the screen.
 *
 * This is a second, independent layer from the IP watermark. That one answers "which connection";
 * this one answers "which account", which is the question an investigation actually starts from.
 *
 * Three things shaped the design, in this order:
 *
 * 1. It has to survive being cropped. Whoever leaks a screenshot crops it, so the mark is a small
 *    tile repeated across the whole viewport rather than one symbol placed somewhere. Any clipping
 *    wider than two tiles contains at least one whole tile, and every tile carries the entire
 *    payload, so there is no such thing as catching only part of it.
 *
 * 2. It has to be unreadable on sight. A QR code is the wrong tool twice over: it is large, and it
 *    is designed to be read by anything with a camera. What is stored here is a truncated digest,
 *    not the account, so the pixels name nobody. Turning one back into a person means hashing the
 *    staff roster and looking for the match, which is something Argus can do and a recipient of the
 *    screenshot cannot.
 *
 * 3. It has to be quiet. The cells are laid out so the payload bits are scattered by a fixed
 *    permutation rather than run in reading order, and the alignment pattern is a handful of
 *    constant cells mixed in with them instead of the solid finder bars a QR code needs. So there
 *    are no lines, no corners and no block — just an even stipple at a tone near the page's own.
 *
 * On secrecy of the digest: the input is a Tesseract subject id, which is a UUID. Domain separation
 * is therefore enough and no bundled salt is used, because a salt shipped to the browser is not a
 * secret from the staff this marks. Anyone who could brute force a subject id already knows whose it
 * is; anyone holding only a screenshot has a few dozen opaque bits.
 *
 * There are two formats, and the reader takes both. v1 stored a forty-bit digest behind a CRC-8,
 * so one misread cell threw the whole tile away. Measured on real clips that turned out to be the
 * wrong failure twice over: most reads that were lost had every cell right and were discarded by a
 * corroboration step, and the ones that did have wrong cells had one to three of them, sitting
 * anywhere in the tile rather than among the marginal ones. v2 keeps the geometry, the permutation
 * and the physical cells of the version and sync fields, and spends sixteen of the digest's bits on
 * two Golay (24,12,8) words instead. Each word corrects any three of its cells, and the parity they
 * add means an alignment has to satisfy forty exact bits rather than twenty, so a read out of noise
 * is a million times rarer before anything corroborates it. The measurements are in
 * docs/SCREEN_MARK_V2.md; the painter writes v2, and v1 stays so screenshots already taken still read.
 */

/** Cells along one edge of a tile. */
export const GRID = 8;
/** CSS pixels per cell. */
export const CELL_PX = 8;
/** CSS pixels along one edge of a tile. */
export const TILE_PX = GRID * CELL_PX;
/** Cells in a tile, and therefore bits carried per tile. */
export const CELLS = GRID * GRID;

/** The format the painter writes. The decoder reads every version listed here. */
export const MARK_VERSION = 2;
export type MarkVersion = 1 | 2;
/** Hex characters in a v1 digest: 40 bits, far more than a staff roster needs. */
export const DIGEST_HEX_V1 = 10;
/** Hex characters in a v2 digest: 24 bits, the other 16 having gone to error correction. */
export const DIGEST_HEX_V2 = 6;

const VERSION_BITS = 4;
const SYNC_BITS = 12;
/** Where the sync cells start. The same in both formats, so the head can be read before the version is known. */
const SYNC_AT = CELLS - SYNC_BITS;

const DIGEST_BITS_V1 = DIGEST_HEX_V1 * 4;
const CRC_BITS = 8;
if (VERSION_BITS + DIGEST_BITS_V1 + CRC_BITS !== SYNC_AT) {
  throw new Error('Screen mark v1 layout is inconsistent: the fields do not meet the sync cells.');
}

const GOLAY_INFO_BITS = 12;
const GOLAY_BITS = 24;
const WORD_A_AT = VERSION_BITS;
const WORD_B_AT = WORD_A_AT + GOLAY_BITS;
if (WORD_B_AT + GOLAY_BITS !== SYNC_AT) {
  throw new Error('Screen mark v2 layout is inconsistent: the Golay words do not meet the sync cells.');
}

/**
 * Constant cells that let a decoder find the tile origin in a crop that starts anywhere.
 *
 * Twelve bits scattered through the tile. v2 uses a different pattern from v1 so that a reader
 * which only knows v1 rejects a v2 tile at the sync cells rather than misreading it, and so that
 * the two heads sit eight bits apart at either polarity, which is what lets the v2 head be matched
 * with tolerance without ever landing on a v1 tile (see V1_HEAD and V2_HEAD).
 */
const SYNC_V1 = [1, 0, 1, 1, 0, 0, 1, 0, 0, 1, 1, 0];
const SYNC_V2 = [0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0];

const toBits = (value: number, width: number): number[] =>
  Array.from({ length: width }, (_, index) => (value >> (width - 1 - index)) & 1);

const fromBits = (bits: number[]): number => bits.reduce((total, bit) => total * 2 + bit, 0);

const hex = (value: number, width: number): string => value.toString(16).padStart(width, '0');

const popcount = (value: number): number => {
  let remaining = value >>> 0;
  let count = 0;
  while (remaining) {
    remaining &= remaining - 1;
    count += 1;
  }
  return count;
};

/**
 * The sixteen bits that identify a tile's format: the version field followed by the sync cells.
 *
 * The two heads are eight bits apart, and so are the inverse of one and the other, so with the v2
 * head matched to within three bits and the v1 head matched exactly there is no word, at either
 * polarity, that both paths would accept. v1 gains a version check from this too: its version
 * field was never checked before.
 */
export const V1_HEAD = fromBits([...toBits(1, VERSION_BITS), ...SYNC_V1]);
export const V2_HEAD = fromBits([...toBits(2, VERSION_BITS), ...SYNC_V2]);
/** How many of the sixteen head bits a v2 tile may have wrong. Pinned by tests, like every budget. */
const HEAD_TOLERANCE = 3;

/**
 * Where each logical bit lives in the tile.
 *
 * Generated rather than written out, so the payload and the sync cells interleave evenly instead of
 * the sync bits sitting in a corner where they would read as a shape.
 */
const PLACEMENT: number[] = (() => {
  const order = Array.from({ length: CELLS }, (_, index) => index);
  // A small deterministic shuffle. The constants are an ordinary LCG; nothing here is secret, it
  // just has to be the same on both sides and it has to scatter.
  let state = 0x9e3779b9;
  for (let i = order.length - 1; i > 0; i -= 1) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    const j = state % (i + 1);
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
})();

/**
 * The physical cells each v2 unit occupies, in logical order.
 *
 * Nothing in the codec needs this; it is for building tiles with known damage — a test or a vector
 * generator that wants three wrong cells in word A, or four in the head, has to know which cells
 * those are without re-deriving the permutation.
 */
export const UNIT_CELLS: { readonly head: readonly number[]; readonly a: readonly number[]; readonly b: readonly number[] } = {
  head: [...PLACEMENT.slice(0, VERSION_BITS), ...PLACEMENT.slice(SYNC_AT)],
  a: PLACEMENT.slice(WORD_A_AT, WORD_B_AT),
  b: PLACEMENT.slice(WORD_B_AT, SYNC_AT),
};

function crc8(bits: number[]): number[] {
  let crc = 0xff;
  for (let index = 0; index < bits.length; index += 8) {
    let byte = 0;
    for (let offset = 0; offset < 8; offset += 1) byte = (byte << 1) | (bits[index + offset] ?? 0);
    crc ^= byte;
    for (let round = 0; round < 8; round += 1) {
      crc = crc & 0x80 ? ((crc << 1) ^ 0x07) & 0xff : (crc << 1) & 0xff;
    }
  }
  return Array.from({ length: CRC_BITS }, (_, index) => (crc >> (CRC_BITS - 1 - index)) & 1);
}

// ── The Golay code ──────────────────────────────────────────────────────────

/**
 * The parity half of the extended binary Golay code's generator, `G = [I | B]`.
 *
 * Twelve rows of twelve bits, MSB first, in the standard MacWilliams–Sloane form. Twelve message
 * bits become a twenty-four bit word by appending the XOR of the rows selected by the set message
 * bits, and any codeword differs from any other in at least eight places, which is what lets three
 * wrong cells be put back with certainty. B is symmetric, so the check matrix is `[B | I]` and a
 * received word's syndrome is simply the parity of its message half XOR its parity half.
 *
 * These numbers are the format: a wrong bit here is a different code, and one that a port would
 * silently disagree with. buildGolaySyndromeTable proves the rows are the Golay code every time
 * the module loads, so a mistranscription cannot ship.
 */
export const GOLAY_B: readonly number[] = [
  0b110111000101, 0b101110001011, 0b011100010111, 0b111000101101,
  0b110001011011, 0b100010110111, 0b000101101111, 0b001011011101,
  0b010110111001, 0b101101110001, 0b011011100011, 0b111111111110,
];

const parityOver = (rows: readonly number[], message: number): number => {
  let parity = 0;
  for (let index = 0; index < GOLAY_INFO_BITS; index += 1) {
    if ((message >> (GOLAY_INFO_BITS - 1 - index)) & 1) parity ^= rows[index];
  }
  return parity;
};

/** The twelve parity bits for a twelve-bit message. */
export function golayParity(m12: number): number {
  return parityOver(GOLAY_B, m12 & 0xfff);
}

const golayEncode = (m12: number): number => (m12 << GOLAY_INFO_BITS) | golayParity(m12);

/** The weights a Golay codeword can have. Any other weight means these are not the Golay rows. */
const GOLAY_WEIGHTS = [0, 8, 12, 16, 24];

/**
 * Builds the decoding table: syndrome in, error pattern out.
 *
 * Every pattern of up to three errors in twenty-four bits — 2325 of them — has its own syndrome,
 * so a received word's syndrome names the cells to flip back, and the remaining 1771 syndromes are
 * damage beyond what the code can undo. The table is built by enumerating those patterns in
 * order of weight and then position, and the builder checks two things as it goes: that no
 * syndrome is claimed twice, and that the codewords weigh exactly 0, 8, 12, 16 or 24. Either
 * failing means the rows are not the Golay code, and the module refuses to load rather than decode
 * marks into something that is not what was painted.
 *
 * Takes the rows as an argument so that the check itself can be tested against corrupted rows.
 */
export function buildGolaySyndromeTable(rows: readonly number[]): Int32Array {
  if (rows.length !== GOLAY_INFO_BITS) {
    throw new Error(`The Golay parity matrix needs ${GOLAY_INFO_BITS} rows, not ${rows.length}.`);
  }
  const syndromeOf = (word: number): number => parityOver(rows, word >>> GOLAY_INFO_BITS) ^ (word & 0xfff);
  const table = new Int32Array(1 << GOLAY_INFO_BITS).fill(-1);
  const claim = (pattern: number) => {
    const syndrome = syndromeOf(pattern);
    if (table[syndrome] !== -1) {
      throw new Error('These are not the Golay rows: two correctable error patterns share a syndrome.');
    }
    table[syndrome] = pattern;
  };
  const bit = (position: number): number => 1 << (GOLAY_BITS - 1 - position);

  claim(0);
  for (let i = 0; i < GOLAY_BITS; i += 1) claim(bit(i));
  for (let i = 0; i < GOLAY_BITS; i += 1) {
    for (let j = i + 1; j < GOLAY_BITS; j += 1) claim(bit(i) | bit(j));
  }
  for (let i = 0; i < GOLAY_BITS; i += 1) {
    for (let j = i + 1; j < GOLAY_BITS; j += 1) {
      for (let k = j + 1; k < GOLAY_BITS; k += 1) claim(bit(i) | bit(j) | bit(k));
    }
  }

  const weights = new Set<number>();
  for (let message = 0; message < 1 << GOLAY_INFO_BITS; message += 1) {
    weights.add(popcount((message << GOLAY_INFO_BITS) | parityOver(rows, message)));
  }
  if (weights.size !== GOLAY_WEIGHTS.length || !GOLAY_WEIGHTS.every((weight) => weights.has(weight))) {
    throw new Error(`These are not the Golay rows: codeword weights are {${[...weights].sort((a, b) => a - b).join(', ')}}.`);
  }
  return table;
}

/** Syndrome to error pattern, 4096 entries; -1 where the damage is beyond correction. */
export const GOLAY_SYNDROME: Int32Array = buildGolaySyndromeTable(GOLAY_B);

/**
 * Corrects up to three errors in a twenty-four bit word and returns the message it carried.
 *
 * Hard decisions only: a cell is a dot or it is not, and the table says which cells to put back.
 * Beyond three errors the syndrome is one the table does not know, and the answer is null rather
 * than the nearest guess — four wrong cells are never quietly corrected to a different message.
 */
export function golayDecode(word24: number): { info: number; errors: number } | null {
  const word = word24 & 0xffffff;
  const pattern = GOLAY_SYNDROME[golayParity(word >>> GOLAY_INFO_BITS) ^ (word & 0xfff)];
  if (pattern < 0) return null;
  return { info: (word ^ pattern) >>> GOLAY_INFO_BITS, errors: popcount(pattern) };
}

// ── The digest and the tile ─────────────────────────────────────────────────

const isMarkVersion = (version: number): version is MarkVersion => version === 1 || version === 2;

const digestHexFor = (version: MarkVersion): number => (version === 1 ? DIGEST_HEX_V1 : DIGEST_HEX_V2);

/**
 * The bits stored for one account, as hex. Never the account itself.
 *
 * Each version hashes under its own label, so a v1 digest and a v2 digest of the same subject are
 * unrelated strings and a roster walk has to know which one it is looking for.
 */
export function deriveDigest(subject: string, version: MarkVersion = MARK_VERSION): string {
  const normalized = subject.trim().toLowerCase();
  if (!normalized) throw new Error('A screen mark needs a subject to derive from.');
  return sha256(`argus.screenmark.v${version}|${normalized}`).slice(0, digestHexFor(version));
}

function digestBits(digestHex: string, version: MarkVersion): number[] {
  const width = digestHexFor(version);
  if (!new RegExp(`^[0-9a-f]{${width}}$`, 'i').test(digestHex)) {
    throw new Error(`Not a v${version} screen mark digest: ${digestHex}`);
  }
  return [...digestHex.toLowerCase()].flatMap((character) => toBits(parseInt(character, 16), 4));
}

/** The v1 stream: version, digest, checksum over both, then the sync cells. */
function payloadBitsV1(digestHex: string): number[] {
  const head = [...toBits(1, VERSION_BITS), ...digestBits(digestHex, 1)];
  return [...head, ...crc8(head), ...SYNC_V1];
}

/** The v2 stream: version, then the digest as two Golay words of twelve bits each, then the sync cells. */
function payloadBitsV2(digestHex: string): number[] {
  const digest = digestBits(digestHex, 2);
  const wordA = golayEncode(fromBits(digest.slice(0, GOLAY_INFO_BITS)));
  const wordB = golayEncode(fromBits(digest.slice(GOLAY_INFO_BITS)));
  return [...toBits(2, VERSION_BITS), ...toBits(wordA, GOLAY_BITS), ...toBits(wordB, GOLAY_BITS), ...SYNC_V2];
}

/** One tile, as cells in row-major order. `true` means the cell carries a dot. */
export function encodeTile(digestHex: string, version: MarkVersion = MARK_VERSION): boolean[] {
  const bits = version === 1 ? payloadBitsV1(digestHex) : payloadBitsV2(digestHex);
  const cells = new Array<boolean>(CELLS).fill(false);
  bits.forEach((bit, index) => { cells[PLACEMENT[index]] = bit === 1; });
  return cells;
}

export interface DecodedMark {
  version: MarkVersion;
  digest: string;
  /** Cells that were wrong and put back. Always 0 for v1, which corrects nothing. */
  errors: number;
  /** Where those cells were: the sixteen-bit head, Golay word A, Golay word B. */
  units: { head: number; a: number; b: number };
}

/** The v1 path: the checksum over the version and digest has to match exactly. */
function decodeV1(bits: number[]): DecodedMark | null {
  const checked = bits.slice(0, VERSION_BITS + DIGEST_BITS_V1);
  const checksum = bits.slice(VERSION_BITS + DIGEST_BITS_V1, SYNC_AT);
  const expected = crc8(checked);
  for (let index = 0; index < CRC_BITS; index += 1) {
    if (checksum[index] !== expected[index]) return null;
  }
  const digest = Array.from({ length: DIGEST_HEX_V1 }, (_, index) =>
    hex(fromBits(checked.slice(VERSION_BITS + index * 4, VERSION_BITS + index * 4 + 4)), 1)).join('');
  return { version: 1, digest, errors: 0, units: { head: 0, a: 0, b: 0 } };
}

/**
 * Reads one tile back, or says that it is not one.
 *
 * Returns null rather than a guess: a reader tries every alignment, so it needs a hard answer to
 * tell the right one from the other four thousand. A tile whose head is exactly v1's takes the v1
 * path, where the checksum has to match outright. Anything else is tried as v2: the head may be
 * within three bits of v2's, each Golay word may carry up to three wrong cells, and the total put
 * back across all three has to fit `maxErrors`. The default of zero means a caller that has not
 * thought about budgets gets the exact forty-bit check; a reader that has measured what it can
 * afford passes a larger one and reports what it spent.
 */
export function decodeTile(cells: boolean[], maxErrors = 0): DecodedMark | null {
  if (cells.length !== CELLS) return null;
  const bits = PLACEMENT.map((cell) => (cells[cell] ? 1 : 0));
  const head = fromBits([...bits.slice(0, VERSION_BITS), ...bits.slice(SYNC_AT)]);

  if (head === V1_HEAD) return decodeV1(bits);

  const headErrors = popcount(head ^ V2_HEAD);
  if (headErrors > HEAD_TOLERANCE) return null;
  const a = golayDecode(fromBits(bits.slice(WORD_A_AT, WORD_B_AT)));
  if (!a) return null;
  const b = golayDecode(fromBits(bits.slice(WORD_B_AT, SYNC_AT)));
  if (!b) return null;
  const errors = headErrors + a.errors + b.errors;
  if (errors > maxErrors) return null;

  return {
    version: 2,
    digest: hex(a.info, DIGEST_HEX_V2 / 2) + hex(b.info, DIGEST_HEX_V2 / 2),
    errors,
    units: { head: headErrors, a: a.errors, b: b.errors },
  };
}

/**
 * Finds the accounts a digest belongs to by hashing the roster.
 *
 * The mapping only runs in this direction. There is nothing to look up and no table to leak: the
 * answer is recomputed from the staff list each time it is asked for, under the derivation the
 * mark's version names.
 *
 * Every match is returned, not the first. A v2 digest is twenty-four bits, and across a roster of
 * five hundred there is a chance under one percent that two people share one; that is an ambiguity
 * between named staff to be shown as such, never a reason to pick one of them.
 */
export function matchDigest<T>(
  mark: { version: number; digest: string }, roster: T[], subjectOf: (entry: T) => string | null | undefined,
): T[] {
  const { version } = mark;
  if (!isMarkVersion(version)) return [];
  const wanted = mark.digest.toLowerCase();
  const matches: T[] = [];
  for (const entry of roster) {
    const subject = subjectOf(entry);
    if (!subject) continue;
    try {
      if (deriveDigest(subject, version) === wanted) matches.push(entry);
    } catch { /* An entry with no usable subject simply does not match. */ }
  }
  return matches;
}
